import { zipSync } from "fflate";
import { saveByteArray } from "../utils/fileUtils";
import {
  registerDownload,
  updateDownloadProgress,
  completeDownload,
  removeDownload,
} from "../utils/download-manager";
let isCancelled = false;
let abortController = null;
async function fetchDicomStudy(studyUrl, headers, onProgress) {
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  const { signal } = abortController;

  try {
    const response = await fetch(studyUrl, { headers, signal });
    const reader = response.body.getReader();
    const chunks = [];
    let receivedLength = 0;

    const contentLength = response.headers.get("Content-Length");
    const totalSize = contentLength ? parseInt(contentLength, 10) : null;

    let done;
    while (!done && !isCancelled) {
      const { value, done: isDone } = await reader.read();
      done = isDone;
      if (value) {
        chunks.push(value);
        receivedLength += value.length;

        if (onProgress && totalSize) {
          const percentComplete = (receivedLength / totalSize) * 100;
          onProgress(percentComplete);
        }
      }
    }

    if (isCancelled) {
      console.log("Download was cancelled");
      return [];
    }

    const byteArray = new Uint8Array(receivedLength);
    let position = 0;
    for (const chunk of chunks) {
      byteArray.set(chunk, position);
      position += chunk.length;
    }

    const boundary = getBoundary(response.headers.get("Content-Type"));
    const dicomParts = parseMultipart(byteArray, boundary);
    return dicomParts.map((part) => part.content);
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("Download was aborted by the user.");
    } else {
      console.error("Error fetching DICOM study:", error);
    }
    return [];
  }
}

function getBoundary(contentType) {
  const boundaryMatch = /boundary="?([^;"]+)"?/.exec(contentType);
  return boundaryMatch ? boundaryMatch[1] : null;
}

function parseMultipart(byteArray, boundary) {
  const boundaryBytes = new TextEncoder().encode("--" + boundary);
  const newlineBytes = new TextEncoder().encode("\r\n");
  const parts = [];
  let start = findBoundary(byteArray, boundaryBytes, 0);

  while (start !== -1) {
    const end = findBoundary(
      byteArray,
      boundaryBytes,
      start + boundaryBytes.length
    );
    if (end === -1) {
      break;
    }
    const partBytes = byteArray.subarray(
      start + boundaryBytes.length + newlineBytes.length,
      end - newlineBytes.length
    );
    const headersEndIndex =
      findBoundary(partBytes, newlineBytes, 0) + newlineBytes.length;
    const content = partBytes.subarray(headersEndIndex);
    parts.push({ content });
    start = end;
  }
  return parts;
}

function findBoundary(byteArray, boundaryBytes, fromIndex) {
  for (let i = fromIndex; i <= byteArray.length - boundaryBytes.length; i++) {
    if (boundaryBytes.every((byte, index) => byteArray[i + index] === byte)) {
      return i;
    }
  }
  return -1;
}

export const downloadZippedStudy = async (
  studyUrl,
  headers,
  zipFileName,
  onProgress
) => {
  const dicomFiles = await fetchDicomStudy(studyUrl, headers, onProgress);
  if (dicomFiles.length === 0) return;

  const filesObject = {};
  dicomFiles.forEach((file, index) => {
    const fileName = (index + 1).toString().padStart(4, "0");
    filesObject[`${fileName}.dcm`] = file;
  });

  const zipped = zipSync(filesObject);
  saveByteArray(zipped, zipFileName, "application/zip");
};

export const cancelDownload = () => {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
};

export const resetCancelDownload = () => {
  isCancelled = false;
  abortController = new AbortController();
};

// Map to track multiple concurrent downloads by download ID
const activeDownloads = new Map();

// Export function to get active downloads count
export function getActiveDownloadsCount() {
  return activeDownloads.size;
}

// Export function to get all active download IDs
export function getActiveDownloadIds() {
  return Array.from(activeDownloads.keys());
}

// Threshold for using direct download vs in-memory download (1MB)
// Files larger than this will use browser's native download to avoid memory issues
const LARGE_FILE_THRESHOLD = 0.5 * 1024 * 1024 * 1024; // 0.5GB in bytes

// Generate a unique download ID
function generateDownloadId() {
  return `download-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Downloads a file using direct browser download (for large files)
 * This avoids loading the entire file into memory
 * Note: This only works if the URL doesn't require authentication headers
 */
const downloadFileDirectly = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = "_blank"; // Open in new tab as fallback
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const fetchZipFile = async (
  zipUrl,
  zipFileName,
  onProgress,
  downloadId,
  knownFileSize
) => {
  // Use provided ID or generate a new one
  const id = downloadId || generateDownloadId();
  
  // Register with global download manager only if not already registered
  // If downloadId is provided, it means the download was already registered elsewhere
  try {
    const { getActiveDownloads } = require("../utils/download-manager");
    const existingDownloads = getActiveDownloads();
    const alreadyRegistered = existingDownloads.some(d => d.id === id);
    
    console.log(`[fetchZipFile] Existing downloads: ${zipFileName}`);

    if (!alreadyRegistered) {
      console.log(`[fetchZipFile] Registering new download: ${id}`);
      registerDownload(id, {
        filename: zipFileName,
        url: zipUrl,
      });
    } else {
      console.log(`[fetchZipFile] Download already registered: ${id}, updating progress only`);
    }
    
    // Wrap progress callback to also update global manager
    const originalProgress = onProgress;
    onProgress = (progress) => {
      if (originalProgress) originalProgress(progress);
      updateDownloadProgress(id, progress, "downloading");
    };
  } catch (error) {
    console.warn("Failed to register/update download with global manager:", error);
  }
  
  // Create a new abort controller for this specific download
  const abortController = new AbortController();
  activeDownloads.set(id, abortController);
  const { signal } = abortController;

  try {
    // Use provided file size, or check via HEAD request if not provided
    // Use a separate abort controller for HEAD request to avoid interference
    let fileSize = knownFileSize ?? null;
    if (fileSize === null) {
      try {
        // Use a timeout for HEAD request to avoid blocking
        const headController = new AbortController();
        const headTimeout = setTimeout(() => {
          headController.abort();
          console.log("HEAD request timeout, proceeding with GET request");
        }, 3000); // 3 second timeout - shorter to avoid blocking
        
        const headResponse = await fetch(zipUrl, { 
          method: "HEAD", 
          signal: headController.signal 
        });
        clearTimeout(headTimeout);
        
        if (headResponse.ok) {
          const contentLength = headResponse.headers.get("Content-Length");
          fileSize = contentLength ? parseInt(contentLength, 10) : null;
          console.log(`HEAD request successful, file size: ${fileSize ? `${(fileSize / 1024 / 1024).toFixed(2)} MB` : 'unknown'}`);
        } else {
          console.warn(`HEAD request returned status ${headResponse.status}, proceeding with GET request`);
          fileSize = null;
        }
      } catch (headError) {
        // If HEAD request fails, proceed with GET and check Content-Length from response
        // This is expected and not an error - we'll get the size from the GET response
        console.log("HEAD request skipped (timeout or error), proceeding with GET request:", headError.name);
        fileSize = null; // Ensure we proceed with GET request
      }
    }

    // For very large files (>threshold), use direct download to avoid memory issues
    if (fileSize && fileSize > LARGE_FILE_THRESHOLD) {
      console.log(`File size (${(fileSize / (1024 * 1024)).toFixed(2)} MB) exceeds threshold. Using direct download.`);
      
      // Show initial progress
      if (onProgress) {
        onProgress(0);
      }
      console.log(`[fetchZipFile] Using direct download: ${zipFileName}`);
      // Use direct download - browser handles it natively
      // Note: This requires the URL to be publicly accessible or use signed URLs
      downloadFileDirectly(zipUrl, zipFileName);
      
      // Simulate progress for UX (we can't track actual progress with direct download)
      // Complete to 100% after a short delay to close the modal
      if (onProgress) {
        let fakeProgress = 0;
        const interval = setInterval(() => {
          fakeProgress += 10;
          if (fakeProgress >= 100) {
            clearInterval(interval);
            onProgress(100); // Complete to 100% to trigger modal close
          } else {
            onProgress(fakeProgress);
          }
        }, 200);
      } else {
        // If no progress callback, still wait a bit before returning
        // to give the browser time to start the download
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Clean up and return
      activeDownloads.delete(id);
      return id;
    }

    // For smaller files or when file size is unknown, use the streaming approach with progress tracking
    console.log(`Starting GET request for download: ${zipFileName} (ID: ${id})`);
    
    const response = await fetch(zipUrl, { signal });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    console.log(`GET request successful, starting to read response body (ID: ${id})`);

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Response body is not readable");
    }

    const chunks = [];
    let receivedLength = 0;

    const responseContentLength = response.headers.get("Content-Length");
    const totalSize = responseContentLength ? parseInt(responseContentLength, 10) : fileSize;
    
    console.log(`Download size: ${totalSize ? `${(totalSize / 1024 / 1024).toFixed(2)} MB` : 'unknown'} (ID: ${id})`);

    let done = false;
    while (!done && !isCancelled) {
      const { value, done: isDone } = await reader.read();
      done = isDone;
      
      if (value) {
        chunks.push(value);
        receivedLength += value.length;

        if (onProgress && totalSize) {
          const percentComplete = (receivedLength / totalSize) * 100;
          onProgress(percentComplete);
        }
      }
    }

    if (isCancelled) {
      console.log(`Download was cancelled (ID: ${id})`);
      activeDownloads.delete(id);
      throw new Error("Download was cancelled");
    }

    console.log(`Download complete, received ${(receivedLength / 1024 / 1024).toFixed(2)} MB, creating blob (ID: ${id})`);

    // For large files: Use Blob constructor directly instead of creating intermediate Uint8Array
    // This is more memory-efficient as Blob can reference chunks without copying them
    // until the blob is actually used
    const blob = new Blob(chunks, { type: "application/zip" });
    console.log(`Blob created, saving file: ${zipFileName} (ID: ${id})`);
    saveByteArray(blob, zipFileName, "application/zip");
    
    // Clean up chunks reference to help GC (though Blob may still reference them)
    chunks.length = 0;
    
    if (onProgress) {
      onProgress(100);
    }
    
    console.log(`Download and save complete: ${zipFileName} (ID: ${id})`);
    
    // Update global download manager - mark as completed
    // Also mark any downloads with matching filename as complete
    try {
      console.log(`[fetchZipFile] Marking download as completed: ${id}`);
      const { getActiveDownloads } = require("../utils/download-manager");
      const allDownloads = getActiveDownloads();
      
      // Mark this download as complete
      completeDownload(id, true);
      
      // Also mark any downloads with matching filename but different ID
      // This handles cases where PatientStudies registered with a different ID
      const matchingDownloads = allDownloads.filter(d => 
        d.id !== id && 
        d.filename === zipFileName &&
        d.status === "downloading"
      );
      
      matchingDownloads.forEach(download => {
        console.log(`[fetchZipFile] Marking matching download as completed: ${download.id} (same filename: ${zipFileName})`);
        completeDownload(download.id, true);
      });
    } catch (error) {
      console.warn("Failed to update download manager:", error);
    }
    
    // Clean up on success
    activeDownloads.delete(id);
    return id;
  } catch (error) {
    // Update global download manager on error
    try {
      completeDownload(id, false);
    } catch (managerError) {
      console.warn("Failed to update download manager:", managerError);
    }
    
    // Clean up on error
    activeDownloads.delete(id);
    
    if (error instanceof Error && error.name === "AbortError") {
      console.log(`Download ${id} was aborted by the user.`);
    } else {
      console.error(`Error fetching zip file ${id}:`, error);
    }
    throw error;
  }
};

/**
 * Cancel a specific download by ID
 */
export const cancelDownloadById = async (downloadId) => {
  const controller = activeDownloads.get(downloadId);
  if (controller) {
    controller.abort();
    activeDownloads.delete(downloadId);
    
    // Update global download manager
    try {
      removeDownload(downloadId);
    } catch (error) {
      console.warn("Failed to update download manager:", error);
    }
    
    console.log(`Cancelled download: ${downloadId}`);
  }
};

/**
 * Cancel all active downloads
 */
export const cancelAllDownloads = () => {
  activeDownloads.forEach((controller, id) => {
    controller.abort();
    console.log(`Cancelled download: ${id}`);
  });
  activeDownloads.clear();
};

// New function to download pre-prepared zip file from backend
export const downloadPreparedZipStudy = async (
  zipUrl,
  headers,
  zipFileName,
  onProgress
) => {
  const zipData = await fetchZipFile(zipUrl, headers, onProgress);
  if (!zipData) return;

  saveByteArray(zipData, zipFileName, "application/zip");
};
